
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";

// Define a type for breadcrumb overrides
interface BreadcrumbOverride {
  path: string;
  title: string;
}

interface BreadcrumbsProps {
  overrides?: BreadcrumbOverride[];
}

const getBreadcrumbs = (pathname: string, overrides?: BreadcrumbOverride[]) => {
  const paths = pathname.split('/').filter(Boolean);
  const breadcrumbs = [];
  let currentPath = '';

  for (let i = 0; i < paths.length; i++) {
    currentPath += `/${paths[i]}`;
    
    // Check if there's an override for this path
    const override = overrides?.find(o => o.path === currentPath);
    
    // Use the override title if available, otherwise format the path segment
    let title = override ? override.title : paths[i].charAt(0).toUpperCase() + paths[i].slice(1);
    
    // Special handling for UUIDs - check if it looks like a UUID
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!override && uuidRegex.test(paths[i])) {
      // This is a UUID, skip it for now - it will be handled by overrides
      title = paths[i];
    }
    
    // Skip "edit" segments as they are implicit
    if (paths[i] === 'edit') {
      continue;
    }
    
    // Truncate long titles
    if (title.length > 30) {
      title = title.substring(0, 27) + "...";
    }
    
    // Check if this is the last non-edit segment
    const isLastNonEditSegment = (() => {
      for (let j = i + 1; j < paths.length; j++) {
        if (paths[j] !== 'edit') {
          return false;
        }
      }
      return true;
    })();
    
    breadcrumbs.push({
      title,
      path: currentPath,
      isLast: isLastNonEditSegment
    });
  }

  return breadcrumbs;
};

export function Breadcrumbs({ overrides }: BreadcrumbsProps = {}) {
  const location = useLocation();
  const breadcrumbs = getBreadcrumbs(location.pathname, overrides);

  return (
    <Breadcrumb className="py-2">
      <BreadcrumbList>        
        {breadcrumbs.map((breadcrumb, index) => (
          <BreadcrumbItem key={breadcrumb.path}>
            {breadcrumb.isLast ? (
              <BreadcrumbPage className="max-w-[200px] truncate font-semibold text-foreground">{breadcrumb.title}</BreadcrumbPage>
            ) : (
              <>
                <BreadcrumbLink asChild>
                  <Link to={breadcrumb.path} className="max-w-[200px] truncate block font-medium text-muted-foreground hover:text-foreground">{breadcrumb.title}</Link>
                </BreadcrumbLink>
                {/* Use the separator outside the nested li */}
                {index < breadcrumbs.length - 1 && <BreadcrumbSeparator />}
              </>
            )}
          </BreadcrumbItem>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
